/* ──────────────────────────────────────────────────────────────────────────
   Days for Expiries, shared by the server and the page. Every date here is a
   calendar day (YYYY-MM-DD), and "today" is Melbourne's (Jay, 2026-10-21),
   wherever the server or the browser happens to be.
   ────────────────────────────────────────────────────────────────────────── */

export const RATE_UNITS = ["days", "weeks", "months", "years"] as const;
export type RateUnit = (typeof RATE_UNITS)[number];

export const ZONE = "Australia/Melbourne";

/** Today in Melbourne. */
export function melbourneToday(now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: ZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

const parts = (day: string) => day.split("-").map(Number) as [number, number, number];
const fmt = (date: Date) => date.toISOString().slice(0, 10);

/** Days from one day to another: negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

export function addDays(day: string, n: number): string {
  const [y, m, d] = parts(day);
  return fmt(new Date(Date.UTC(y, m - 1, d + n)));
}

/**
 * A day plus one expiry rate. Months and years keep the day of the month and
 * fall back to the month's last day when it has none, so 31 Jan plus a month
 * is 28 Feb rather than 3 Mar.
 */
export function addRate(day: string, count: number, unit: RateUnit): string {
  if (unit === "days") return addDays(day, count);
  if (unit === "weeks") return addDays(day, count * 7);
  const [y, m, d] = parts(day);
  const months = unit === "months" ? count : count * 12;
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, last));
  return fmt(target);
}

/** "30 days", "1 month", "2 years". */
export function rateLabel(count: number, unit: RateUnit): string {
  return `${count} ${count === 1 ? unit.slice(0, -1) : unit}`;
}
